import { CSSProperties, useEffect, useMemo, useState } from 'react';
import { calculateSurface, getSurfaceDimensions } from '../../shared/calculations/layoutEngine';
import { ProjectState, RoomFixture, SurfaceId, TileData, TilePlacement } from '../../shared/types';
import { useProjectStore } from '../store/useProjectStore';
import { Icon } from './Icon';
import { Room3D } from './Room3D';

const surfaceLabels: Record<SurfaceId, string> = {
  front: 'Передняя',
  left: 'Левая',
  right: 'Правая',
  back: 'Задняя',
  floor: 'Пол',
};

const temperatureColor = (kelvin: number): string => {
  if (kelvin <= 3400) return 'rgba(255, 170, 80, .18)';
  if (kelvin >= 5900) return 'rgba(120, 175, 255, .13)';
  return 'rgba(255, 245, 225, .08)';
};

export const TileImage = ({
  material,
  placement,
}: {
  material: TileData;
  placement: TilePlacement;
}) => {
  const clipId = `clip-${placement.id}`;
  const physicalWidth = material.rotation === 90 || material.rotation === 270
    ? material.tileHeight
    : material.tileWidth;
  const physicalHeight = material.rotation === 90 || material.rotation === 270
    ? material.tileWidth
    : material.tileHeight;
  const physicalX = placement.x - placement.sourceX;
  const physicalY = placement.y - placement.sourceY;
  const centerX = physicalX + physicalWidth / 2;
  const centerY = physicalY + physicalHeight / 2;
  const imageX = centerX - material.tileWidth / 2;
  const imageY = centerY - material.tileHeight / 2;
  const flipped = Boolean(material.mirror) !== Boolean(placement.mirror) ? -1 : 1;
  const scaleX = flipped;
  const scaleY = placement.mirrorY ? -1 : 1;
  const transform = `translate(${centerX} ${centerY}) rotate(${placement.rotation}) scale(${scaleX} ${scaleY}) translate(${-centerX} ${-centerY})`;
  const opacity = material.variationEnabled
    ? 0.96 + ((placement.row + placement.column) % 3) * 0.015
    : 1;

  if (placement.diagonal) {
    // Ёлочка и диагональ: ячейка может быть шире/уже картинки — рисуем по размеру ячейки
    const drawWidth = placement.width || material.tileWidth;
    const drawHeight = placement.height || material.tileHeight;
    const drawCenterX = placement.x + drawWidth / 2;
    const drawCenterY = placement.y + drawHeight / 2;
    const diagonalTransform = `translate(${drawCenterX} ${drawCenterY}) rotate(${placement.rotation}) scale(${scaleX} ${scaleY}) translate(${-drawCenterX} ${-drawCenterY})`;
    return (
      <g transform={diagonalTransform}>
        <image
          href={material.imagePath}
          x={drawCenterX - drawWidth / 2}
          y={drawCenterY - drawHeight / 2}
          width={drawWidth}
          height={drawHeight}
          preserveAspectRatio="xMidYMid slice"
          opacity={opacity}
          data-category={placement.category}
          data-rotation={placement.rotation}
          data-mirror={scaleX === -1 ? 'true' : 'false'}
          data-diagonal="true"
        />
        <rect
          x={drawCenterX - drawWidth / 2}
          y={drawCenterY - drawHeight / 2}
          width={drawWidth}
          height={drawHeight}
          fill="none"
          stroke="rgba(0,0,0,.08)"
          strokeWidth="1"
          vectorEffect="non-scaling-stroke"
        />
      </g>
    );
  }

  return (
    <g>
      <clipPath id={clipId}>
        <rect x={placement.x} y={placement.y} width={placement.width} height={placement.height} />
      </clipPath>
      <image
        href={material.imagePath}
        x={imageX}
        y={imageY}
        width={material.tileWidth}
        height={material.tileHeight}
        preserveAspectRatio="xMidYMid slice"
        clipPath={`url(#${clipId})`}
        transform={transform}
        opacity={opacity}
        data-category={placement.category}
        data-rotation={placement.rotation}
        data-mirror={scaleX === -1 ? 'true' : 'false'}
      />
      <rect
        x={placement.x}
        y={placement.y}
        width={placement.width}
        height={placement.height}
        fill="none"
        stroke="rgba(0,0,0,.08)"
        strokeWidth="1"
        vectorEffect="non-scaling-stroke"
      />
    </g>
  );
};

const SurfaceView = ({ project, zoom }: { project: ProjectState; zoom: number }) => {
  const calculation = useMemo(
    () => calculateSurface(project, project.selectedSurface),
    [project],
  );
  // Обрезаем объекты по границам стены (та же логика, что в layoutEngine)
  const dims = getSurfaceDimensions(project, project.selectedSurface);
  const openings = project.objects
    .filter((object) => object.surfaceId === project.selectedSurface)
    .map((object) => ({
      ...object,
      width: Math.min(object.width, dims.width),
      height: Math.min(object.height, dims.height),
      x: Math.min(Math.max(0, object.x), Math.max(0, dims.width - Math.min(object.width, dims.width))),
      y: Math.min(Math.max(0, object.y), Math.max(0, dims.height - Math.min(object.height, dims.height))),
    }));
  const referenceCanvasWidth = 1000;

  return (
    <div className="surface-stage" style={{ '--canvas-zoom': zoom } as CSSProperties}>
      <svg
        className="surface-canvas"
        viewBox={`0 0 ${calculation.width} ${calculation.height}`}
        style={{ aspectRatio: `${calculation.width} / ${calculation.height}` }}
        role="img"
        aria-label={`Раскладка: ${surfaceLabels[project.selectedSurface]}`}
      >
        <defs>
          <clipPath id="surface-clip">
            <rect width={calculation.width} height={calculation.height} />
          </clipPath>
          <filter id="surface-shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="18" stdDeviation="22" floodOpacity=".18" />
          </filter>
          <linearGradient id="surface-light" x1={project.lighting.direction === 'right' ? '100%' : '0%'} y1="0%" x2={project.lighting.direction === 'right' ? '0%' : '100%'} y2="100%">
            <stop offset="0%" stopColor="white" stopOpacity={project.lighting.intensity / 220} />
            <stop offset="62%" stopColor="white" stopOpacity="0" />
            <stop offset="100%" stopColor="#172026" stopOpacity={(100 - project.lighting.softness) / 360} />
          </linearGradient>
        </defs>
        <g filter="url(#surface-shadow)">
          <rect width={calculation.width} height={calculation.height} fill={project.grout.color} />
          <g clipPath="url(#surface-clip)">
            {calculation.tiles.map((placement) => {
              const selectedTile = project.tiles[placement.category];
              if (!selectedTile) return null;
              return (
                <TileImage
                  key={placement.id}
                  material={selectedTile}
                  placement={placement}
                />
              );
            })}
          </g>
          {openings.map((object) => {
            const top = calculation.height - object.y - object.height;
            if (object.type === 'door') {
              const panelInset = Math.min(120, object.width * 0.16);
              const panelTopH = object.height * 0.42;
              const panelBotH = object.height * 0.38;
              const gapY = Math.min(160, object.height * 0.07);
              return (
                <g key={object.id}>
                  {/* Проём (стена за дверью) */}
                  <rect x={object.x} y={top} width={object.width} height={object.height} fill="#efe9dd" stroke="rgba(20,27,29,.5)" strokeWidth="12" vectorEffect="non-scaling-stroke" />
                  {/* Полотно двери */}
                  <rect x={object.x + 6} y={top + 6} width={object.width - 12} height={object.height - 12} rx="14" fill="#e8e0d2" />
                  {/* Филёнки */}
                  <rect x={object.x + panelInset} y={top + panelInset} width={object.width - panelInset * 2} height={panelTopH} rx="10" fill="#dcd2c0" stroke="#c8bfae" strokeWidth="14" />
                  <rect x={object.x + panelInset} y={top + panelInset + panelTopH + gapY} width={object.width - panelInset * 2} height={panelBotH} rx="10" fill="#dcd2c0" stroke="#c8bfae" strokeWidth="14" />
                  {/* Ручка */}
                  <circle cx={object.x + object.width - panelInset * 0.7} cy={top + object.height / 2} r={Math.max(30, object.width * 0.05)} fill="#b08d57" />
                </g>
              );
            }
            if (object.type === 'window') {
              const frame = Math.max(50, object.width * 0.07);
              return (
                <g key={object.id}>
                  {/* Стекло с лёгким градиентом неба */}
                  <rect x={object.x} y={top} width={object.width} height={object.height} fill="#c8e2f0" stroke="#f2efe8" strokeWidth={frame} vectorEffect="non-scaling-stroke" />
                  {/* Перекладины рамы */}
                  <line x1={object.x + object.width / 2} x2={object.x + object.width / 2} y1={top} y2={top + object.height} stroke="#f2efe8" strokeWidth={frame * 0.8} vectorEffect="non-scaling-stroke" />
                  <line x1={object.x} x2={object.x + object.width} y1={top + object.height / 2} y2={top + object.height / 2} stroke="#f2efe8" strokeWidth={frame * 0.8} vectorEffect="non-scaling-stroke" />
                  {/* Блик */}
                  <line x1={object.x + object.width * 0.2} x2={object.x + object.width * 0.42} y1={top + object.height * 0.16} y2={top + object.height * 0.5} stroke="white" strokeWidth={frame * 0.5} opacity="0.5" vectorEffect="non-scaling-stroke" />
                  {/* Подоконник */}
                  <rect x={object.x - frame} y={top + object.height} width={object.width + frame * 2} height={frame * 0.6} fill="#e9e5db" />
                </g>
              );
            }
            return (
              <g key={object.id}>
                <rect x={object.x} y={top} width={object.width} height={object.height} fill="#5d6362" stroke="rgba(20,27,29,.48)" strokeWidth="12" vectorEffect="non-scaling-stroke" />
              </g>
            );
          })}
          <rect width={calculation.width} height={calculation.height} fill="url(#surface-light)" pointerEvents="none" />
        </g>
        <g className="dimension-lines">
          <line x1="0" y1={calculation.height + 30} x2={calculation.width} y2={calculation.height + 30} />
        </g>
      </svg>
      <div className="dimension-label dimension-width">{calculation.width} мм</div>
      <div className="dimension-label dimension-height">{calculation.height} мм</div>
      <div className="canvas-scale">М 1:{Math.max(5, Math.round(calculation.width / referenceCanvasWidth / 5) * 5)}</div>
    </div>
  );
};

interface VisualizerProps {
  onGenerate: () => void;
}

export const Visualizer = ({ onGenerate }: VisualizerProps) => {
  const { project, updateProject } = useProjectStore();
  const [zoom, setZoom] = useState(1);
  const [walkthrough, setWalkthrough] = useState(false);
  const dimensions = getSurfaceDimensions(project, project.selectedSurface);

  useEffect(() => {
    if (!walkthrough) return undefined;
    const exitOnEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setWalkthrough(false);
    };
    window.addEventListener('keydown', exitOnEsc);
    return () => window.removeEventListener('keydown', exitOnEsc);
  }, [walkthrough]);

  const setSurface = (surface: SurfaceId) =>
    updateProject((draft) => {
      draft.selectedSurface = surface;
      return draft;
    });

  const setViewMode = (viewMode: ProjectState['viewMode']) =>
    updateProject((draft) => {
      draft.viewMode = viewMode;
      return draft;
    });

  // Перетаскивание/поворот сантехники в 3D — одна запись в истории на перетаскивание
  const moveFixture = (fixture: RoomFixture) =>
    updateProject((draft) => {
      const target = (draft.fixtures ?? []).find((item) => item.id === fixture.id);
      if (target) {
        target.x = fixture.x;
        target.z = fixture.z;
        target.rotation = fixture.rotation;
      }
      return draft;
    });

  const removeFixture = (id: string) =>
    updateProject((draft) => {
      draft.fixtures = (draft.fixtures ?? []).filter((item) => item.id !== id);
      return draft;
    });

  const visualStyle = {
    filter: `brightness(${project.lighting.brightness}%)`,
    '--temperature-overlay': temperatureColor(project.lighting.temperature),
  } as CSSProperties;

  return (
    <main className="visualizer">
      <div className="visualizer-toolbar">
        <div className="surface-tabs">
          {(Object.keys(surfaceLabels) as SurfaceId[]).map((surface) => (
            <button type="button" key={surface} className={project.selectedSurface === surface ? 'active' : ''} onClick={() => setSurface(surface)}>
              {surfaceLabels[surface]}
            </button>
          ))}
        </div>
        <div className="view-switch">
          <button type="button" className={project.viewMode === 'surface' ? 'active' : ''} onClick={() => setViewMode('surface')}><Icon name="layers" /> 2D</button>
          <button type="button" className={project.viewMode === 'room' ? 'active' : ''} onClick={() => setViewMode('room')}><Icon name="cube" /> 3D</button>
          {project.viewMode === 'room' && (
            <button
              type="button"
              className={walkthrough ? 'active' : ''}
              title="Осмотр изнутри: WASD/стрелки — движение, мышь — взгляд"
              onClick={() => setWalkthrough((value) => !value)}
            >
              <Icon name="present" /> Изнутри
            </button>
          )}
        </div>
      </div>

      <div className="canvas-viewport">
        <div className="canvas-backdrop" style={visualStyle}>
          {project.viewMode === 'surface' ? <SurfaceView project={project} zoom={zoom} /> : (
            <Room3D
              project={project}
              zoom={zoom}
              walkthrough={walkthrough}
              onFixtureMove={moveFixture}
              onFixtureDelete={removeFixture}
            />
          )}
          <div className="temperature-overlay" />
        </div>
        <div className="viewport-title">
          <span>{surfaceLabels[project.selectedSurface]}</span>
          <small>{dimensions.width} × {dimensions.height} мм</small>
        </div>
        <div className="zoom-controls">
          <button type="button" onClick={() => setZoom((value) => Math.max(0.6, value - 0.1))} aria-label="Уменьшить"><Icon name="zoomOut" /></button>
          <button type="button" onClick={() => setZoom(1)} aria-label="По размеру"><Icon name="fit" /></button>
          <button type="button" onClick={() => setZoom((value) => Math.min(1.8, value + 0.1))} aria-label="Увеличить"><Icon name="zoomIn" /></button>
          <span>{Math.round(zoom * 100)}%</span>
        </div>
        <button type="button" className="generate-button" onClick={onGenerate}>
          <span className="button-shine" />
          <Icon name="cube" />
          Создать визуализацию
        </button>
      </div>
    </main>
  );
};
